import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/misc';
import { useTask } from '@/features/crm/api';
import { TaskDetailsDialog } from '@/features/crm/task-details-dialog';
import { TaskDialog } from '@/features/crm/task-dialog';
import { useUserOptions } from '@/hooks/use-user-options';
import { formatDateTime, timeAgo } from '@/lib/format';
import type { NotificationRow } from '@/lib/types';

/**
 * What a notification opens.
 *
 * A notification about a task opens the task itself — its details, its
 * updates and the box to add one — so answering "Task assigned: rrr" does not
 * mean leaving the page you were on to go and find it. Anything else opens a
 * plain card with the full message and a way through to where it came from.
 */
export function NotificationDialog({
  notification,
  onClose,
}: {
  notification: NotificationRow;
  onClose: () => void;
}) {
  if (notification.entityType === 'task' && notification.entityId) {
    return <TaskNotification notification={notification} taskId={notification.entityId} onClose={onClose} />;
  }
  return <PlainNotification notification={notification} onClose={onClose} />;
}

function TaskNotification({
  notification,
  taskId,
  onClose,
}: {
  notification: NotificationRow;
  taskId: string;
  onClose: () => void;
}) {
  const { data: task, isLoading, isError } = useTask(taskId);
  const [editing, setEditing] = useState(false);

  const { data: users = [] } = useUserOptions();
  const nameOf = useMemo(() => {
    const names = new Map(users.map((user) => [user.id, user.name]));
    return (id: string) => names.get(id) ?? 'Someone';
  }, [users]);

  // The task was deleted, or taken off this person since: say what the
  // notification said rather than show an error for a message they can read.
  if (isError) return <PlainNotification notification={notification} onClose={onClose} />;

  if (isLoading || !task) {
    return (
      <Dialog open onOpenChange={(open) => !open && onClose()}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{notification.title}</DialogTitle>
            <DialogDescription>Opening the task…</DialogDescription>
          </DialogHeader>
          <Skeleton className="h-48" />
        </DialogContent>
      </Dialog>
    );
  }

  if (editing) return <TaskDialog task={task} onClose={onClose} />;

  return (
    <TaskDetailsDialog task={task} nameOf={nameOf} onClose={onClose} onEdit={() => setEditing(true)} />
  );
}

function PlainNotification({
  notification,
  onClose,
}: {
  notification: NotificationRow;
  onClose: () => void;
}) {
  const navigate = useNavigate();

  return (
    <Dialog open onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="pe-6 leading-snug">{notification.title}</DialogTitle>
          <DialogDescription>
            {formatDateTime(notification.createdAt)} · {timeAgo(notification.createdAt)}
          </DialogDescription>
        </DialogHeader>

        {notification.body ? (
          <p className="whitespace-pre-wrap rounded-md bg-muted/50 p-3 text-sm">{notification.body}</p>
        ) : (
          <p className="text-sm text-muted-foreground">Nothing more was sent with this one.</p>
        )}

        <DialogFooter className="gap-2 sm:gap-2">
          <Button variant="outline" onClick={onClose}>
            Close
          </Button>
          {notification.link && (
            <Button
              onClick={() => {
                onClose();
                navigate(notification.link!);
              }}
            >
              Open
              <ArrowRight className="h-4 w-4" />
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
